import * as RadixCheckbox from '@radix-ui/react-checkbox'
import { useId } from 'react'
import { cn } from '@/lib/utils'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons } from '@/ds/icons'

export interface DsCheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  description?: string
  error?: string | null
  disabled?: boolean
  /** Accessible name when there is no visible label (a row's select box). */
  semanticLabel?: string
  className?: string
}

export function DsCheckbox({ checked, onChange, label, description, error, disabled, semanticLabel, className }: DsCheckboxProps) {
  const id = useId()
  return (
    <div className={cn('flex flex-col', className)}>
      <label htmlFor={id} className={cn('flex min-h-tap cursor-pointer items-start gap-3 py-2', disabled && 'cursor-not-allowed opacity-60')}>
        <RadixCheckbox.Root
          id={id}
          checked={checked}
          onCheckedChange={(v) => onChange(v === true)}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-label={label ? undefined : semanticLabel}
          className={cn(
            'mt-0.5 inline-flex size-[22px] shrink-0 items-center justify-center rounded-sm border-[1.5px] bg-card outline-none transition-colors motion-fast focus-visible:ring-focus',
            checked ? 'border-primary bg-primary text-primary-foreground' : error ? 'border-danger' : 'border-input',
          )}
        >
          <RadixCheckbox.Indicator>
            <DsIconGlyph icon={DsIcons.check} size={16} className="stroke-[2.5]" />
          </RadixCheckbox.Indicator>
        </RadixCheckbox.Root>
        {(label || description) && (
          <span className="flex flex-col">
            {label && <span className="text-body text-foreground">{label}</span>}
            {description && <span className="text-small text-muted-foreground">{description}</span>}
          </span>
        )}
      </label>
      {error && (
        <p role="alert" className="text-caption text-danger-soft-foreground">
          {error}
        </p>
      )}
    </div>
  )
}
